'use client';

import React from 'react';
import { useBranchCode } from '@/components/SiteHostProvider';
import Image from 'next/image';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { regionalizePath } from '@/lib/config/branches-config';

interface Project {
  id?: string;
  slug?: string;
  title?: string;
  name?: string;
  NAME?: string;
  imageUrl?: string;
  PREVIEW_PICTURE?: string;
  PREVIEW_PICTURE_URL?: string;
  description?: string;
  PREVIEW_TEXT?: string;
}

interface RelatedPortfolioProps {
  projects: Project[];
  currentProjectSlug?: string;
  title?: string;
  subtitle?: string;
  basePath?: string;
  hideDescription?: boolean;
  hideCta?: boolean;
  cardTitleClassName?: string;
  cardContentClassName?: string;
  sectionPaddingClassName?: string;
  headerSpacingClassName?: string;
  titleClassName?: string;
  subtitleClassName?: string;
}

export default function RelatedPortfolio({
  projects,
  currentProjectSlug,
  title = 'Другие наши проекты',
  subtitle = 'Возможно, вас также заинтересуют эти проекты',
  basePath = '/portfolio',
  hideDescription = false,
  hideCta = false,
  cardTitleClassName,
  cardContentClassName,
  sectionPaddingClassName,
  headerSpacingClassName,
  titleClassName,
  subtitleClassName,
}: RelatedPortfolioProps) {
  const pathname = usePathname() || '/';
  // Филиал страницы: по хосту (BRANCH_ROUTING=host) или по /geo/<code>/.
  const pageBranchCode = useBranchCode();
  const currentBranchCode = pageBranchCode;
  const resolvedCardTitleClassName =
    cardTitleClassName ||
    'text-lg md:text-xl font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors';
  const resolvedCardContentClassName = cardContentClassName || 'p-4 md:p-6';
  const resolvedSectionPaddingClassName = sectionPaddingClassName || 'py-8 md:py-12 lg:py-16';
  const resolvedHeaderSpacingClassName = headerSpacingClassName || 'mb-8 md:mb-12';
  const resolvedTitleClassName =
    titleClassName || 'text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4';
  const resolvedSubtitleClassName =
    subtitleClassName || 'text-gray-600 dark:text-gray-400 text-base md:text-lg';
  
  // Фильтруем текущий проект и ограничиваем до 4
  const filteredProjects = projects
    .filter((project) => {
      const slug = project.slug || project.id;
      return slug !== currentProjectSlug;
    })
    .slice(0, 4);

  if (filteredProjects.length === 0) {
    return null;
  }

  return (
    <section className={resolvedSectionPaddingClassName}>
      <div className={resolvedHeaderSpacingClassName}>
        <h2 className={resolvedTitleClassName}>{title}</h2>
        {subtitle && (
          <p className={resolvedSubtitleClassName}>{subtitle}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {filteredProjects.map((project, index) => {
          const projectId = project.id || project.slug || `project-${index}`;
          const projectTitle = project.title || project.NAME || project.name || '';
          const projectImage =
            project.imageUrl ||
            project.PREVIEW_PICTURE ||
            project.PREVIEW_PICTURE_URL ||
            '';
          const projectSlug = project.slug || projectId;
          const projectDescription =
            project.description || project.PREVIEW_TEXT || '';

          return (
            <a key={projectId} href={regionalizePath(`${basePath}/${projectSlug}`, currentBranchCode)}>
              <Card className="border border-slate-200 dark:border-slate-800 h-full hover:shadow-lg transition-all duration-200 group">
                <CardContent className="p-0">
                  <div className="relative aspect-video overflow-hidden rounded-t-lg">
                    {projectImage ? (
                      <Image
                        alt={projectTitle}
                        src={projectImage}
                        width={400}
                        height={300}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        unoptimized
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                        <span className="text-gray-400 text-sm">Нет изображения</span>
                      </div>
                    )}
                  </div>
                  <div className={resolvedCardContentClassName}>
                    <h3 className={resolvedCardTitleClassName}>
                      {projectTitle}
                    </h3>
                    {projectDescription && !hideDescription && (
                      <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                        {projectDescription}
                      </p>
                    )}
                    {!hideCta && (
                      <div className="flex items-center text-blue-600 dark:text-blue-400 font-medium text-sm md:text-base group-hover:gap-2 transition-all">
                        Подробнее
                        <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>
    </section>
  );
}
